// Thin per-app wrapper around the shared @lf/analytics-events package.
// Reads env-var configuration once and exposes:
//   - initializeAnalytics(): call once at boot from App.js
//   - usePageTracking(): React hook firing `page_view` on route change
// Plus re-exports the public surface from @lf/analytics-events so
// call sites have a single import path.
//
// Per design §D *Provider abstraction*, no call site outside this
// file may import gtag/react-ga4 directly. The vendor adapter lives
// in @lf/analytics-events.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    init,
    pageView,
    routeIdForPath,
    setConsent,
    identifyByProfile,
    track,
    setAppRole,
} from '@lf/analytics-events';

const APP_ROLE = 'parent';

export function analyticsProvider(env = process.env) {
    if (env.REACT_APP_ANALYTICS_PROVIDER) {
        return env.REACT_APP_ANALYTICS_PROVIDER;
    }
    // A missing production build flag must not silently replace real
    // analytics with the in-process test recorder. Local development and
    // tests remain isolated unless they explicitly opt into GA4.
    return env.NODE_ENV === 'production' ? 'ga4' : 'memory';
}

export function analyticsConfig(env = process.env) {
    return {
        provider: analyticsProvider(env),
        measurementId: env.REACT_APP_GA4_ID || 'G-QBTFWYNDL1',
        environment: env.REACT_APP_LF_ENV
            || (env.NODE_ENV === 'production' ? 'prod' : 'local'),
        appRole: APP_ROLE,
    };
}

export function analyticsConsent(cookie = '') {
    const consentCookie = cookie.split(';').map((part) => part.trim())
        .find((part) => part.startsWith('lf_consent='));
    if (!consentCookie) return 'denied';
    try {
        return decodeURIComponent(consentCookie.slice('lf_consent='.length)) === 'granted'
            ? 'granted'
            : 'denied';
    } catch (_error) {
        return 'denied';
    }
}

export function hasAnalyticsConsentChoice(cookie = '') {
    return cookie.split(';').map((part) => part.trim())
        .some((part) => part.startsWith('lf_consent='));
}

// The marketing module (cards/indexV1/marketingAnalytics.js) renders a
// consent banner of its own, #lf-consent-banner. Both can end up on screen
// at once: App.js imports IndexPageV1 eagerly, so that module's IIFE
// auto-runs while imports are still being evaluated — before index.js
// reaches initializeAnalytics(). Its bootstrapGtagIfNeeded() check
// therefore finds no window.gtag, concludes it is running standalone (as
// it does inside the baked homepage snapshot), and renders its banner on
// every route of the parent app, not just /www_v1.
//
// Inside the React app the product banner is the right owner: its buttons
// call setConsent() and so drive the analytics-events consent state
// machine, which the marketing banner's own click handler does not. Both
// write the same lf_consent cookie, so taking the marketing one down
// loses nothing. Done on every show — not once at init — because the
// import-order guarantee above is subtle enough that a later refactor
// (lazy-loading IndexPageV1, say) could quietly reverse it.
const CONSENT_BANNER_ID = 'lf-product-consent';
const MARKETING_CONSENT_BANNER_ID = 'lf-consent-banner';

export function dismissMarketingConsentBanner() {
    if (typeof document === 'undefined') return;
    const marketingBanner = document.getElementById(MARKETING_CONSENT_BANNER_ID);
    if (marketingBanner) marketingBanner.remove();
}

export function showAnalyticsConsentBanner() {
    if (typeof document === 'undefined' || !document.body) return;
    dismissMarketingConsentBanner();
    if (document.getElementById(CONSENT_BANNER_ID)) return;
    const banner = document.createElement('div');
    banner.id = CONSENT_BANNER_ID;
    banner.setAttribute('role', 'dialog');
    banner.setAttribute('aria-label', 'Cookie preferences');
    banner.style.cssText = 'position:fixed;left:16px;right:16px;bottom:16px;z-index:2147483000;max-width:560px;margin:auto;background:#282846;color:#fff;padding:14px 16px;border-radius:14px;box-shadow:0 10px 40px rgba(0,0,0,.25);font:13px/1.45 sans-serif;display:flex;flex-wrap:wrap;align-items:center;gap:10px';
    banner.innerHTML = '<div style="flex:1 1 240px">We use cookies for analytics to improve Learnforest. See our <a href="/privacy.html" style="color:#a6e4f9;text-decoration:underline">Privacy Policy</a>.</div><button type="button" data-consent="granted">Accept</button><button type="button" data-consent="denied">Decline</button>';
    banner.querySelectorAll('button').forEach((button) => {
        button.style.cssText = 'border:1px solid rgba(255,255,255,.4);border-radius:10px;padding:8px 14px;font-weight:600;cursor:pointer';
        button.addEventListener('click', () => {
            const consent = button.getAttribute('data-consent') === 'granted'
                ? 'granted' : 'denied';
            document.cookie = `lf_consent=${consent}; max-age=${60 * 60 * 24 * 365}; path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure; domain=.learnforest.app' : ''}`;
            setConsent(consent);
            banner.remove();
        });
    });
    document.body.appendChild(banner);
}

export function initializeAnalytics() {
    const config = analyticsConfig();
    const provider = config.provider;
    init({
        ...config,
        // debug_mode routes events to GA4's DebugView and excludes them
        // from standard / Realtime reports. Tie it to an explicit env
        // var (or ?ga_debug=1 query param) rather than NODE_ENV so dev
        // builds against a real measurement_id show up in Realtime
        // queries by default.
        debug: process.env.REACT_APP_GA_DEBUG === '1'
            || (typeof window !== 'undefined'
                && window.location && window.location.search
                && /[?&]ga_debug=1/.test(window.location.search)),
    });
    const cookie = typeof document !== 'undefined' ? document.cookie : '';
    const consent = analyticsConsent(cookie);
    setConsent(consent);
    if (provider === 'ga4' && !hasAnalyticsConsentChoice(cookie)) {
        showAnalyticsConsentBanner();
    }

    // The marketing module (cards/indexV1/marketingAnalytics.js) is
    // dependency-free vanilla JS that calls window.gtag('event', ...)
    // directly, both on /www_v1 and inside the static homepage snapshot.
    // The ga4 adapter wires window.gtag itself; for memory/off providers
    // we install a shim so those direct gtag calls land in the SDK
    // pipeline (and from there in the in-process buffer that e2e tests
    // assert on). Setting dataLayer + gtag also makes the marketing
    // module's bootstrapGtagIfNeeded() short-circuit, avoiding a
    // duplicate stub install that would clobber this shim.
    if (typeof window !== 'undefined' && provider !== 'ga4') {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag(command) {
            if (command === 'event') {
                const name = arguments[1];
                const params = arguments[2] || {};
                track(name, params);
            }
            // Other gtag commands (consent/config/js/set/get) are no-ops
            // for non-ga4 providers — the SDK owns consent itself, and
            // there's no real gtag.js loader to configure.
        };
    }

    // Test seam — exposed only when REACT_APP_LF_ENV is e2e or local
    // so an e2e test can fire arbitrary events through the same
    // pipeline call sites use. Not present in production builds.
    if (typeof window !== 'undefined'
        && (process.env.REACT_APP_LF_ENV === 'e2e'
            || process.env.REACT_APP_LF_ENV === 'local')) {
        window.__lfTrackForTest__ = (name, params) => track(name, params || {});
    }
}

/**
 * Emit a product pageview per route change.
 *
 * @param {boolean} [enabled] false on routes that are not product usage. The
 *   eval surfaces pass false: internal labelling sessions in a product funnel
 *   are the operator measuring themselves, and §4.13.3's rule — a click that is
 *   not a labelling verdict must not become evidence — runs in this direction
 *   too.
 */
export function usePageTracking(enabled = true) {
    const location = useLocation();
    useEffect(() => {
        if (!enabled) return;
        pageView(routeIdForPath(location.pathname, { appRole: APP_ROLE }), {});
    }, [location, enabled]);
}

export async function setUserId(parentProfileId) {
    if (!parentProfileId) {
        return null;
    }
    return identifyByProfile({
        role: 'parent',
        profileId: parentProfileId,
        salt: process.env.REACT_APP_LF_ANALYTICS_SALT || '',
    });
}

export { track, setConsent, setAppRole };
